// Helper: apertura file allegato via signed URL GCS con fallback su blob download.
// - Tenta GET /api/attachments/{id}/signed-url (staff-only) → apre direttamente da GCS (bypass backend).
// - Se 409 (file legacy Emergent) o altro errore → fallback blob via /download.
// Ritorna un oggetto { url, revoke } (revoke=null per URL firmato, funzione per blob object URL).
import { api } from "@/lib/api";

/**
 * Ottiene un URL apribile per un attachment.
 * @param {string} attachmentId
 * @param {object} opts
 *   - contentType: forza il MIME al blob quando serve
 *   - expiresIn: TTL signed URL (default 900s = 15 min)
 *   - useBlob: se true, salta il signed URL e forza blob (utile per anteprima inline con revocazione controllata)
 * @returns {Promise<{ url: string, revoke: (() => void) | null, backend: "gcs" | "blob" }>}
 */
export async function getAttachmentOpenUrl(attachmentId, { contentType, expiresIn = 900, useBlob = false } = {}) {
  if (!useBlob) {
    try {
      const { data } = await api.get(`/attachments/${attachmentId}/signed-url`, { params: { expires_in: expiresIn } });
      if (data?.url) return { url: data.url, revoke: null, backend: "gcs" };
    } catch (e) {
      // 409 = legacy Emergent → fallback silenzioso. Altri errori (401/403/404) rilanciati.
      const status = e?.response?.status;
      if (status && status !== 409) {
        throw e;
      }
    }
  }
  // Fallback: scarica blob dal backend
  const resp = await api.get(`/attachments/${attachmentId}/download`, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: contentType || resp.data.type || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  return { url, revoke: () => URL.revokeObjectURL(url), backend: "blob" };
}

/**
 * Forza il download (salvataggio file) per un attachment usando signed URL se disponibile.
 * @param {string} attachmentId
 * @param {string} filename - nome file suggerito
 * @param {object} opts (vedi getAttachmentOpenUrl)
 */
export async function downloadAttachment(attachmentId, filename, opts = {}) {
  const { url, revoke, backend } = await getAttachmentOpenUrl(attachmentId, opts);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `attachment-${attachmentId}`;
  // Signed URLs GCS già includono response-content-disposition → il download è forzato
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Piccolo delay prima di revocare l'object URL blob per permettere al browser di iniziare il download
  if (revoke) setTimeout(revoke, 5000);
  return backend;
}
